import { apiClient } from './client';

export interface DashboardStats {
  totalEvents: number;
  upcomingEvents: number;
  totalGuests: number;
  unpaidTotal: number;
  recentInquiries: number;
  revenueThisYear: number;
  totalProfit: number;
}

export interface UpcomingEvent {
  id: string;
  name: string;
  startAt: string;
  endAt: string | null;
  status: string;
  eventType: string | null;
  venue: string | null;
  /** Sum of maleCount+femaleCount+kidCount across all confirmed guests. */
  confirmedGuests: number;
  /** Sum of maleCount+femaleCount+kidCount across all guests. */
  totalGuests: number;
  /** People in parties that have not answered yet (rsvpStatus 'invited'). */
  pendingGuests: number;
  /** People in parties that answered maybe. */
  maybeGuests: number;
  /** People in parties that declined. */
  declinedGuests: number;
  /** Sum of expense item prices for this event. */
  totalExpenses: number;
  /** Cash the client still needs to provide (Option 1; same formula as client ledger). */
  balanceDue: number;
  /** Resolved presigned URL of the event-specific logo (null when none). */
  logoUrl: string | null;
}

export interface RecentInquiry {
  id: string;
  name: string;
  email: string;
  eventType: string | null;
  createdAt: string;
}

export interface OverduePayment {
  id: string;
  eventName: string;
  eventId: string;
  category: string | null;
  vendor: string | null;
  /** Total expense price (kept for back-compat). */
  amount: number;
  /** Sum of payments recorded against this expense. */
  paid: number;
  /** Outstanding balance (amount - paid). Always > 0 here, since fully-paid items are filtered out server-side. */
  balance: number;
  dueDate: string | null;
}

export interface UpcomingChecklistItem {
  id: string;
  title: string;
  status: string;
  isDone: boolean;
  dueDate: string | null;
  assigneeName: string | null;
  eventName: string | null;
  eventId: string | null;
  checklistTitle: string | null;
  sectionName: string | null;
}

export interface UpcomingPlanningTodo {
  id: string;
  title: string;
  status: string;
  isDone: boolean;
  dueDate: string | null;
  assigneeName: string | null;
  eventName: string | null;
  eventId: string | null;
  planningListTitle: string | null;
  itemLabel: string | null;
}

export interface DashboardData {
  stats: DashboardStats;
  upcomingEvents: UpcomingEvent[];
  recentInquiries: RecentInquiry[];
  overduePayments: OverduePayment[];
  upcomingChecklistItems: UpcomingChecklistItem[];
  upcomingPlanningTodos: UpcomingPlanningTodo[];
}

export async function fetchDashboardStats(): Promise<DashboardData> {
  const { data } = await apiClient.get<DashboardData>('/dashboard/stats');
  return data;
}
